import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  IInvoice,
  IInvoiceCreatePayload,
  IInvoiceUpdatePayload,
  IInvoiceStatusPayload,
  IInvoicePaymentPayload,
  IInvoiceDeletePayload,
  IInvoiceFromQuotePayload,
  IInvoiceStats,
} from "../../../types/api/invoice";
import {
  listInvoices,
  invoiceDetail,
  invoiceStats,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  updateInvoiceStatus,
  recordInvoicePayment,
  createInvoiceFromQuote,
  createInvoiceFromPO,
  generateInvoiceShareLink,
  createCreditNote,
} from "./callbacks";

// ── List ─────────────────────────────────────────────────────────────────────

export const useInvoiceList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IInvoice[]>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponseWithQData<IInvoice[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IInvoice[]>, Error>({
    queryKey: [API_ENDPOINTS.INVOICE_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listInvoices(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
            setQueryParams(params as QData);
    },
  };
};

// ── Detail ───────────────────────────────────────────────────────────────────

export const useInvoiceDetail = (
  id: string | number,
  options?: Omit<
    UseQueryOptions<TResponse<IInvoice>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponse<IInvoice>, Error> => {
  return useQuery<TResponse<IInvoice>, Error>({
    queryKey: [API_ENDPOINTS.INVOICE_DETAIL, id],
    queryFn: async () => {
      const res = await invoiceDetail(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

// ── Stats ─────────────────────────────────────────────────────────────────────

export const useInvoiceStats = (
  options?: Omit<
    UseQueryOptions<TResponse<IInvoiceStats>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponse<IInvoiceStats>, Error> => {
  return useQuery<TResponse<IInvoiceStats>, Error>({
    queryKey: [API_ENDPOINTS.INVOICE_STATS],
    queryFn: async () => {
      const res = await invoiceStats();
      return res.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

// ── Create ────────────────────────────────────────────────────────────────────

export const useInvoiceCreate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IInvoice>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IInvoice>,
    unknown,
    IInvoiceCreatePayload
  > = {},
): UseMutationResult<TResponse<IInvoice>, unknown, IInvoiceCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IInvoice>, unknown, IInvoiceCreatePayload>({
    mutationFn: async (data) => {
      const res = await createInvoice(data);
      return res.data;
    },

    onSuccess: (res, ...rest) => {
      const created = res.data as IInvoice;

      queryClient.setQueryData(
        [API_ENDPOINTS.INVOICE_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;

          return {
            ...old,
            data: [created, ...old.data],
            extra: {
              ...old.extra,
              total: (old.extra?.total ?? 0) + 1,
            },
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.INVOICE_STATS],
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};

// ── Update ────────────────────────────────────────────────────────────────────

export const useInvoiceUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IInvoice>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IInvoice>,
    unknown,
    IInvoiceUpdatePayload
  > = {},
): UseMutationResult<TResponse<IInvoice>, unknown, IInvoiceUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IInvoice>, unknown, IInvoiceUpdatePayload>({
    mutationFn: async (data) => {
      const res = await updateInvoice(data);
      return res.data;
    },

    onSuccess: (res, ...rest) => {
      const updated = res.data as IInvoice;

      // ── LIST UPDATE ─────────────────────────────
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.INVOICE_LIST, { ...qData, total: null }] },
        (old: any) => {
          if (!old?.data) return old;

          return {
            ...old,
            data: old.data.map((inv: IInvoice) =>
              inv.id === updated.id ? updated : inv,
            ),
          };
        },
      );

      // ── DETAIL UPDATE ───────────────────────────
      queryClient.setQueryData(
        [API_ENDPOINTS.INVOICE_DETAIL, updated.id],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: updated,
          };
        },
      );

      // ── STATS REFRESH ───────────────────────────
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.INVOICE_STATS],
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};

// ── Delete ────────────────────────────────────────────────────────────────────

export const useInvoiceDelete = (
  options?: UseMutationOptions<TResponse<null>, Error, IInvoiceDeletePayload> & {
    onSuccess?: () => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<null>, Error, IInvoiceDeletePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<null>, Error, IInvoiceDeletePayload>({
    mutationFn: async (data) => (await deleteInvoice(data)).data,
    onSuccess: (_res, variables) => {
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.INVOICE_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.filter((inv: IInvoice) => inv.id !== variables.id),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_STATS] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ── Status Update ─────────────────────────────────────────────────────────────

export const useInvoiceUpdateStatus = (
  options?: UseMutationOptions<
    TResponse<IInvoice>,
    Error,
    IInvoiceStatusPayload
  > & { onSuccess?: (data: IInvoice) => void; onError?: (e: any) => void },
): UseMutationResult<TResponse<IInvoice>, Error, IInvoiceStatusPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IInvoice>, Error, IInvoiceStatusPayload>({
    mutationFn: async (data) => (await updateInvoiceStatus(data)).data,
    onSuccess: (res) => {
      const updated = res.data as IInvoice;
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.INVOICE_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((inv: IInvoice) =>
              inv.id === updated.id ? { key: updated.id, ...updated } : inv,
            ),
          };
        },
      );
      queryClient.setQueryData(
        [API_ENDPOINTS.INVOICE_DETAIL, updated.id],
        (old: any) => (old ? { ...old, data: updated } : old),
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_STATS] });
      options?.onSuccess?.(updated);
    },
    onError: options?.onError,
  });
};

// ── Record Payment ────────────────────────────────────────────────────────────

export const useInvoiceRecordPayment = (
  options?: UseMutationOptions<
    TResponse<IInvoice>,
    Error,
    IInvoicePaymentPayload
  > & { onSuccess?: (data: IInvoice) => void; onError?: (e: any) => void },
): UseMutationResult<TResponse<IInvoice>, Error, IInvoicePaymentPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IInvoice>, Error, IInvoicePaymentPayload>({
    mutationFn: async (data) => (await recordInvoicePayment(data)).data,
    onSuccess: (res) => {
      const updated = res.data as IInvoice;
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.INVOICE_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((inv: IInvoice) =>
              inv.id === updated.id ? { key: updated.id, ...updated } : inv,
            ),
          };
        },
      );
      queryClient.setQueryData(
        [API_ENDPOINTS.INVOICE_DETAIL, updated.id],
        (old: any) => (old ? { ...old, data: updated } : old),
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_STATS] });
      options?.onSuccess?.(updated);
    },
    onError: options?.onError,
  });
};

// ── Generate Share Link ───────────────────────────────────────────────────────

export const useInvoiceGenerateShareLink = (
  options?: UseMutationOptions<TResponse<{ token: string }>, Error, number> & {
    onSuccess?: (token: string) => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<{ token: string }>, Error, number> => {
  return useMutation<TResponse<{ token: string }>, Error, number>({
    mutationFn: async (id) => (await generateInvoiceShareLink(id)).data,
    onSuccess: (res) => options?.onSuccess?.(res.data?.token ?? ""),
    onError: options?.onError,
  });
};

// ── Create from PO ────────────────────────────────────────────────────────────

export const useInvoiceFromPO = (
  options?: { onSuccess?: (data: IInvoice) => void; onError?: (e: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<IInvoice>, Error, { po_id: number; title?: string }>({
    mutationFn: async (data) => (await createInvoiceFromPO(data)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_STATS] });
      options?.onSuccess?.(res.data as IInvoice);
    },
    onError: options?.onError,
  });
};

// ── Create Credit Note ────────────────────────────────────────────────────────

export const useInvoiceCreateCreditNote = (
  options?: { onSuccess?: (data: IInvoice) => void; onError?: (e: any) => void },
) => {
  const queryClient = useQueryClient();
  return useMutation<TResponse<IInvoice>, Error, { original_invoice_id: number; reason?: string; line_items?: any[] }>({
    mutationFn: async (data) => (await createCreditNote(data)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_STATS] });
      options?.onSuccess?.(res.data as IInvoice);
    },
    onError: options?.onError,
  });
};

// ── Create from Quote ─────────────────────────────────────────────────────────

export const useInvoiceFromQuote = (
  options?: UseMutationOptions<
    TResponse<IInvoice>,
    Error,
    IInvoiceFromQuotePayload
  > & { onSuccess?: (data: IInvoice) => void; onError?: (e: any) => void },
): UseMutationResult<TResponse<IInvoice>, Error, IInvoiceFromQuotePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IInvoice>, Error, IInvoiceFromQuotePayload>({
    mutationFn: async (data) => (await createInvoiceFromQuote(data)).data,
    onSuccess: (res) => {
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.INVOICE_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return { ...old, data: [{ key: res.data?.id, ...res.data }, ...old.data] };
        },
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.INVOICE_STATS] });
      options?.onSuccess?.(res.data as IInvoice);
    },
    onError: options?.onError,
  });
};
